import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import {
  Bell, CheckCheck, UserPlus, ClipboardCheck, CalendarClock, Circle,
} from "lucide-react";

const ICONS = {
  task_assigned: ClipboardCheck,
  task_completed: CheckCheck,
  client_assigned: UserPlus,
  followup_reminder: CalendarClock,
};

export default function Notifications() {
  const [notes, setNotes] = useState([]);
  const nav = useNavigate();

  const load = async () => setNotes((await api.get("/notifications")).data);
  useEffect(() => { load(); }, []);

  const markAll = async () => { await api.put("/notifications/read-all"); load(); };
  const open = async (n) => {
    if (!n.read) await api.put(`/notifications/${n.id}/read`);
    if (n.link) nav(n.link); else load();
  };

  return (
    <>
      <PageHeader
        title="Notifications"
        subtitle="Task assignments, completions, customer allocations and follow-up reminders."
        testId="notifications-page"
        actions={
          <Button variant="outline" onClick={markAll} className="rounded-sm gap-2" data-testid="mark-all-read-button">
            <CheckCheck className="h-4 w-4" /> Mark all read
          </Button>
        }
      />
      {notes.length === 0 ? (
        <Empty icon={Bell} title="No notifications" sub="You're all caught up." />
      ) : (
        <div className="border border-border bg-card rounded-sm divide-y divide-border">
          {notes.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <button key={n.id} onClick={() => open(n)}
                className="w-full flex items-start gap-3 p-4 text-left hover:bg-secondary/40 transition-colors"
                data-testid={`notification-${n.id}`}>
                <div className={`h-9 w-9 flex items-center justify-center rounded-sm shrink-0 ${n.read ? "bg-secondary text-muted-foreground" : "bg-primary/10 text-primary"}`}>
                  <Icon className="h-4.5 w-4.5 h-[18px] w-[18px]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${n.read ? "text-muted-foreground" : "font-medium"}`}>{n.message}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
                </div>
                {!n.read && <Circle className="h-2.5 w-2.5 fill-primary text-primary shrink-0 mt-1.5" />}
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}
